import React, { FunctionComponent, ReactElement, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useHistory, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { useMedia } from 'react-media-match';
import Button from '@empiriecom/module-components/Button';
import Headline from '@empiriecom/module-components/Headline';
import WishListProvider from '@empiriecom/module-components/WishListProvider';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import getIcon from '@empiriecom/module-components/utils/getIcon';
import { PageWrapper } from '@/src/components/PageContainer';
import { RatingReview, Rating } from '@/src/interfaces/Rating';
import { FragmentConfiguration } from '@/config';
import { StarsWrapper, RatingStars } from '../RatingsSummary';
import { RatingCard } from '../RatingCard';
import RatingsListBuyBox from './RatingsListBuyBox';
import RatingsListPaging from './RatingsListPaging';
import RatingsListSummary from './RatingsListSummary';
import CreateRating from '../CreateRating';
import CreateRatingContextProvider from '../CreateRating/CreateRatingContext';
import { CollapsibleContextProvider } from '../../Card/CollapsibleContext';
import { useProduct, useSeo } from '../../ProductContext';
import { SeoElement } from '../../SeoElement';
import { AddToBasketModal } from '../../AddToBasket';

const messages = defineMessages({
  seoTitle: {
    defaultMessage: 'Kundenbewertungen',
    id: 'customer-ratings.seo.title',
  },
});

const BackButtonWrapper = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #dadada;
  margin-bottom: 0.5rem;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const SummaryContainer = styled.div<{ mobile: boolean }>`
  ${({ mobile }) => !mobile && 'padding: 0 1.75rem;'}
`;

const SummaryWrapper = styled.div<{ mobile: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  ${({ mobile }) => mobile && 'flex-direction: column;'}
`;

const RatingsListHeadline = styled.div`
  margin-top: 1rem;
  > h1 {
    font-size: 1.125rem;
    line-height: normal;
    font-weight: bold;
  }
  .ratings-headline-link {
    text-decoration: none;
  }
`;

const StyledStarsWrapper = styled(StarsWrapper)`
  display: inline-flex;
  padding: 0;
`;

const RatingsListWrapper = styled.div<{ mobile: boolean }>`
  border: 1px solid #f4f4f4;
  background: #f4f4f4;
  padding: ${({ mobile }) => (mobile ? '0 0 0.75rem 0;' : '0.75rem 0;')};
  margin-top: ${({ mobile }) => (mobile ? '0.75rem;' : '1.5rem;')};
`;

const fetchRatings = (fetchUrl: string): Promise<any> =>
  fetch(fetchUrl, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    method: 'GET',
  });

export const RatingsList: FunctionComponent = (): ReactElement => {
  const [response, setResponse] = useState<Rating>(null);
  const [ratings, setRatings] = useState<RatingReview[]>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isPaging, setIsPaging] = useState<boolean>(false);
  const [tagFilter, setTagFilter] = useState<string>('');
  const [fetchUrl, setFetchUrl] = useState<string>('');
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const intl = useIntl();
  const config: FragmentConfiguration = useConfig();
  const { product } = useProduct();
  const { error: seoError, isLoading: seoLoading, seoData } = useSeo();
  const ArrowLeft = getIcon('ArrowMediumLeft');
  const history = useHistory();

  useEffect(() =>
    setTagFilter(
      history.location.pathname
        ?.split('/')
        .filter((part) => part.match(/f-\d+/))
        .join(''),
    ),
  );

  useEffect(() => {
    if (product !== null && typeof product !== 'undefined') {
      setFetchUrl(
        config.customerRatings.values.ratingPaginationURL
          .replace('<endcount>', config.customerRatings.values.pagingCount)
          .replace('<productid>', product.akl)
          .replace('<startcount>', '0')
          .concat(tagFilter !== '' ? `?filter=${tagFilter.replace('f-', '')}` : ''),
      );
    }
    setIsLoading(true);
  }, [product]);

  useEffect(() => {
    if (!isPaging && product !== null && typeof product !== 'undefined') {
      setFetchUrl(
        config.customerRatings.values.ratingPaginationURL
          .replace('<endcount>', config.customerRatings.values.pagingCount)
          .replace('<productid>', product.akl)
          .replace('<startcount>', '0')
          .concat(tagFilter !== '' ? `?filter=${tagFilter.replace('f-', '')}` : ''),
      );
      setIsLoading(true);
    }
  }, [tagFilter]);

  useEffect(() => {
    if (isPaging && product !== null && typeof product !== 'undefined') {
      setFetchUrl(
        config.customerRatings.values.ratingPaginationURL
          .replace('<endcount>', config.customerRatings.values.pagingCount)
          .replace('<productid>', product.akl)
          .replace(
            '<startcount>',
            (
              parseInt(config.customerRatings.values.pagingCount, 10) *
              (response ? response.paging.currentPage : 0)
            ).toFixed(),
          )
          .concat(tagFilter !== '' ? `?filter=${tagFilter.replace('f-', '')}` : ''),
      );
      setIsLoading(true);
    }
  }, [isPaging]);

  useEffect(() => {
    if (isLoading && fetchUrl !== '') {
      fetchRatings(fetchUrl)
        .then((resp: Response) => {
          if (resp.ok) {
            return resp.json() as Promise<Rating>;
          }
          return null;
        })
        .then((data) => {
          let newRatings: RatingReview[] = [];
          if (isPaging) {
            newRatings = newRatings.concat(ratings);
          }
          if (data.product !== null) {
            data.product.reviews.map((review) => newRatings.push(review));
          }
          setResponse(data);
          setRatings(newRatings);
          setIsLoading(false);
          setIsPaging(false);
        });
    }
  }, [fetchUrl]);

  // prepare back URL
  let backURL = history.location.pathname.replace(
    `/rating${tagFilter !== '' ? `/${tagFilter}` : ''}`,
    '',
  );
  if (backURL.slice(-1) === '/') {
    backURL = backURL.replace(/.$/, '');
  }

  // trigger scroll to top on initial render
  useEffect(() => {
    if (window !== undefined) {
      window.scrollTo(0, 0);
    }
  }, []);
  const titlePrefix = intl.formatMessage(messages.seoTitle);
  let titleSuffix: string = product?.name.concat(' | ').concat(config.company.seoTitleName);
  if (seoData?.title !== 'undefined' && seoData?.title !== '') {
    titleSuffix = seoData?.title;
  }
  const title = `${titlePrefix} | ${titleSuffix}`;

  let pathname: string = '';
  if (typeof window !== 'undefined') {
    pathname = window.location.pathname;
    if (pathname.search(/(f-\d+\/{0,1})/) !== -1) {
      pathname = pathname.replace(/(f-\d+\/{0,1})/, '');
    }
    if (pathname.slice(-1) === '/') {
      pathname = pathname.replace(/.$/, '');
    }
  }

  return (
    <>
      <Helmet>
        {title !== '' ? <title>{title}</title> : ''}
        <meta name="robots" content="noodp,noindex,follow" />
        {typeof window !== 'undefined' && (
          <link
            href={`${window.location.origin}${
              pathname !== '' ? pathname : window.location.pathname
            }`}
            rel="canonical"
          />
        )}
      </Helmet>
      {response && (
        <PageWrapper data-testid="ratings-list">
          <CollapsibleContextProvider>
            <BackButtonWrapper>
              <Button
                layout="default"
                asRouterLink
                to={{
                  ...history.location,
                  pathname: backURL,
                }}
              >
                <ArrowLeft />
                <FormattedMessage
                  id="customer-ratings.back-to-product"
                  defaultMessage="Zurück zum Artikel"
                />
              </Button>
            </BackButtonWrapper>
            <WishListProvider>
              <SummaryContainer mobile={isMobile}>
                {!isMobile && (
                  <RatingsListHeadline>
                    <Headline level={1}>
                      <FormattedMessage
                        id="customer-ratings.overview.headline"
                        defaultMessage="Kundenbewertungen zum Artikel"
                      />
                      <span>&#160;</span>
                      <Link
                        className="ratings-headline-link"
                        to={{
                          ...history.location,
                          pathname: backURL,
                        }}
                      >
                        &quot;{response.product.name}&quot;
                      </Link>
                    </Headline>
                  </RatingsListHeadline>
                )}
                {!isMobile && (
                  <StyledStarsWrapper>
                    <RatingStars
                      iconType={config.customerRatings.iconType}
                      maskWidth={`${parseFloat(response.product.avgRating.toFixed(1)) * 20}%`}
                    />
                    <span>({response.product.reviewCount})</span>
                  </StyledStarsWrapper>
                )}
                <SummaryWrapper mobile={isMobile}>
                  <RatingsListSummary response={response} />
                  <RatingsListBuyBox />
                </SummaryWrapper>
              </SummaryContainer>
              <RatingsListWrapper mobile={isMobile}>
                <CreateRatingContextProvider>
                  <CreateRating />
                </CreateRatingContextProvider>
                {ratings &&
                  ratings.length > 0 &&
                  ratings.map((rating: RatingReview) =>
                    rating !== null ? <RatingCard key={rating.id} rating={rating} /> : null,
                  )}
                <RatingsListPaging
                  response={response}
                  isLoading={isLoading}
                  onPagingClick={() => setIsPaging(true)}
                />
              </RatingsListWrapper>
              {config.seo.enabled && !seoLoading && !seoError && seoData && (
                <SeoElement data={seoData} />
              )}
            </WishListProvider>
          </CollapsibleContextProvider>
          <AddToBasketModal />
        </PageWrapper>
      )}
    </>
  );
};

export default RatingsList;
